import {
  GridItem,
  IconTagV3,
  OuraSymbolIcon,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';

export interface Profile {
  bioKey: MessageKey;
  enabled?: boolean;
  media: ProfileMedia;
  name: MessageKey;
  titleKey?: MessageKey;
  certifiedAdvisorKey: MessageKey;
}

interface ProfileMedia {
  alt: MessageKey;
  src: string;
  desktopSrc: string;
}

interface RowImageProps {
  readonly media: Profile['media'];
  readonly index: number;
  readonly certifiedAdvisorKey: Profile['certifiedAdvisorKey'];
}

const RowImage = ({ media, index, certifiedAdvisorKey }: RowImageProps) => {
  const t = useTranslations();

  return (
    <>
      <div
        className="relative aspect-1080/1202 size-full"
        data-cy="profile-image"
      >
        <ArtDirectionImage
          srcSet={[
            {
              src: media.src,
              media: '(max-width: 768px)',
            },
            {
              src: media.desktopSrc,
              media: '(min-width: 769px)',
            },
          ]}
          localizedAlt={media.alt}
          fill
          className="object-contain"
          priority={index === 0}
        />
      </div>
      <div className="relative w-full" data-cy="icon-tag-overlay">
        <div className="absolute -mt-20 flex w-full justify-center md:ml-5 md:justify-normal">
          <IconTagV3
            icon={<OuraSymbolIcon />}
            iconTagText={t(certifiedAdvisorKey)}
          />
        </div>
      </div>
    </>
  );
};

interface RowTextProps {
  readonly name: Profile['name'];
  readonly titleKey: Profile['titleKey'];
  readonly bioKey: Profile['bioKey'];
}

const RowText = ({ name, titleKey, bioKey }: RowTextProps) => {
  const t = useTranslations();
  return (
    <>
      <TypographyV3
        className="pt-4 text-4xl md:pt-0 md:text-5xl"
        weight="light"
        data-cy="profile-name"
      >
        {t(name)}
      </TypographyV3>
      {titleKey && (
        <TypographyV3 className="mb-6 text-sm" data-cy="profile-title">
          {t(titleKey)}
        </TypographyV3>
      )}
      <TypographyV3 data-cy="profile-bio">{t.rich(bioKey)}</TypographyV3>
    </>
  );
};
interface MABRowProps {
  readonly name: Profile['name'];
  readonly titleKey: Profile['titleKey'];
  readonly bioKey: Profile['bioKey'];
  readonly isEven: boolean;
  readonly media: Profile['media'];
  readonly index: number;
  readonly certifiedAdvisorKey: Profile['certifiedAdvisorKey'];
}

const MABRow = ({
  media,
  titleKey,
  bioKey,
  name,
  isEven,
  index,
  certifiedAdvisorKey,
}: MABRowProps) => {
  return (
    <>
      <GridItem
        colStart={{
          sm: 'full',
          md: isEven ? 'full' : 14,
          lg: isEven ? 3 : 14,
        }}
        colEnd={{
          sm: 'full',
          md: isEven ? 12 : 'full',
          lg: isEven ? 12 : 23,
        }}
      >
        <RowImage
          media={media}
          index={index}
          certifiedAdvisorKey={certifiedAdvisorKey}
        />
      </GridItem>
      <GridItem
        colStart={{
          sm: 'main',
          md: isEven ? 14 : 'full',
          lg: isEven ? 14 : 3,
        }}
        colEnd={{
          sm: 'main',
          md: isEven ? 'full' : 12,
          lg: isEven ? 23 : 12,
        }}
        className={cx({ 'md:order-first': !isEven })}
      >
        <RowText name={name} titleKey={titleKey} bioKey={bioKey} />
      </GridItem>
    </>
  );
};
export default MABRow;
